import {Injectable} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {Actions, Effect, ofType} from '@ngrx/effects';
import {
  DeleteFloor,
  DeleteFloorComplete,
  DeleteFloorError,
  FloorPlanActionTypes,
  GetOrganizationFloors,
  GetOrganizationFloorsComplete,
  GetOrganizationFloorsError,
  PostFloor,
  PostFloorComplete,
  PostFloorError,
  PostFloorPlanImage,
  PostFloorPlanImageComplete,
  PostFloorPlanImageError,
  PutFloorDevices,
  PutFloorDevicesComplete,
  PutFloorDevicesError
} from '../actions/floor-plan.actions';
import {catchError, exhaustMap, map, mergeMap, tap} from 'rxjs/operators';
import {
  DeleteFloorRequest,
  DeleteFloorResponse,
  Floor,
  GetOrganizationFloorsRequest,
  PostFloorPlanImageRequest,
  PostFloorRequest,
  PostFloorResponse,
  PutFloorDevicesRequest
} from '../models/floor';
import {FloorPlanService} from '../services/floor-plan.service';
import {forkJoin, of} from 'rxjs';
import {Simple200Response} from '../models/general';


@Injectable()
export class FloorPlanEffects {

  @Effect()
  getOrganizationFloors$ = this.actions$.pipe(
    ofType(FloorPlanActionTypes.GetOrganizationFloors),
    map((action: GetOrganizationFloors) => action.payload),
    exhaustMap((getOrganizationFloorsRequest: GetOrganizationFloorsRequest) =>
      this.floorPlanService.getOrganizationFloors(getOrganizationFloorsRequest)
        .pipe(
          map((floors: Floor[]) => new GetOrganizationFloorsComplete(floors)),
          catchError((error: HttpErrorResponse) => of(new GetOrganizationFloorsError({
            errorMessage: error.error.message
          })))
        )
    )
  );

  @Effect()
  postFloor$ = this.actions$.pipe(
    ofType(FloorPlanActionTypes.PostFloor),
    map((action: PostFloor) => action.payload),
    exhaustMap((postFloorRequest: PostFloorRequest) =>
      forkJoin(this.floorPlanService.postFloor(postFloorRequest), of(postFloorRequest.name))
        .pipe(
          tap(([postFloorResponse, name]) => {
            (<any>window).Appcues && (<any>window).Appcues.track('Created floor', {
              floorId: postFloorResponse.id,
              floorName: name
            });
            (<any>window).Appcues && (<any>window).Appcues.show('-LJDuPbQiFMjDxS5xDxj');
          }),
          map(([postFloorResponse, name]: [PostFloorResponse, string]) => {
            return {id: postFloorResponse.id, name};
          }),
          map((floor: Floor) => new PostFloorComplete(floor)),
          catchError((error: HttpErrorResponse) => of(new PostFloorError({
            errorMessage: this.getPostFloorErrorMessage(error)
          })))
        )
    )
  );

  @Effect()
  deleteFloor$ = this.actions$.pipe(
    ofType(FloorPlanActionTypes.DeleteFloor),
    map((action: DeleteFloor) => action.payload),
    exhaustMap((deleteFloorRequest: DeleteFloorRequest) =>
      forkJoin(this.floorPlanService.deleteFloor(deleteFloorRequest), of(deleteFloorRequest.organizationToken))
        .pipe(
          mergeMap(([deleteFloorResponse, organizationToken]: [DeleteFloorResponse, string]) => [
            new DeleteFloorComplete(deleteFloorResponse),
            new GetOrganizationFloors({organizationToken})
          ]),
          catchError((error: HttpErrorResponse) => of(new DeleteFloorError({
            errorMessage: error.error.message
          })))
        )
    )
  );

  @Effect()
  postFloorPlanImage$ = this.actions$.pipe(
    ofType(FloorPlanActionTypes.PostFloorPlanImage),
    map((action: PostFloorPlanImage) => action.payload),
    exhaustMap((postFloorPlanImageRequest: PostFloorPlanImageRequest) =>
      forkJoin(this.floorPlanService.postFloorPlanImage(postFloorPlanImageRequest), of(postFloorPlanImageRequest.organizationToken))
        .pipe(
          mergeMap(([response, organizationToken]: [Simple200Response, string]) => [
            new PostFloorPlanImageComplete(response),
            new GetOrganizationFloors({organizationToken})
          ]),
          catchError((error: HttpErrorResponse) => of(new PostFloorPlanImageError({
            errorMessage: error.error.message
          })))
        )
    )
  );

  @Effect()
  putFloorDevices$ = this.actions$.pipe(
    ofType(FloorPlanActionTypes.PutFloorDevices),
    map((action: PutFloorDevices) => action.payload),
    exhaustMap((putFloorDevicesRequest: PutFloorDevicesRequest) =>
      forkJoin(this.floorPlanService.putFloorDevices(putFloorDevicesRequest), of(putFloorDevicesRequest.organizationToken))
        .pipe(
          mergeMap(([response, organizationToken]: [Simple200Response, string]) => [
            new PutFloorDevicesComplete(response),
            new GetOrganizationFloors({organizationToken})
          ]),
          catchError((error: HttpErrorResponse) => of(new PutFloorDevicesError({
            errorMessage: error.error.message
          })))
        )
    )
  );

  constructor(private actions$: Actions,
              private floorPlanService: FloorPlanService) {
  }

  private getPostFloorErrorMessage(error: HttpErrorResponse): string {
    const errorMsg = {
      422: 'Floor name already exists'
    };

    return errorMsg[error.status] || 'Failed to create floor.\nPlease try again later.';
  }
}
